/**
 * build-graph — pure story-structure graph builder.
 *
 * Derives a directed graph (scenes → scenes) from a set of LunaScript `.ls`
 * sources by scanning each scene's control-flow exits:
 *   - `@gate { ... }` exits: `@next <target>` (routes to another episode) and
 *     `@end <type>` (terminates → a synthetic ending node);
 *   - `@choice { @option <ID> <brave|safe> "<label>" { ... @next <target> } }`
 *     option branches that route onward (edge label = the option text);
 *   - any bare `@next <target>` / `@end <type>` outside a gate (tolerant).
 *
 * This is a STRUCTURAL scan, not a full parser: it reads the raw text with
 * regexes so it stays robust to malformed/partial sources. It NEVER throws —
 * anything it can't understand is simply skipped. A `@next` target that does
 * not match a known scene id still produces an edge to a `missing` placeholder
 * node, so dangling routes are visible in the canvas.
 *
 * The `.ls` text is the single source of truth (design §4.15 / §5.3); the
 * canvas reads this graph read-mostly.
 */
import { buildEpisodeStructure, humanizeRouteCondition } from "./episode-structure.js";

/** Structural kind of a graph node. */
export type StoryNodeKind = "scene" | "ending" | "missing";

/** A node in the story-structure graph. */
export interface StoryNode {
  /** Stable graph id. For scenes this is the scene id; ending/missing nodes
   *  get a synthetic id (see {@link endingNodeId} / {@link missingNodeId}). */
  id: string;
  /** Human-readable label shown on the canvas node. */
  label: string;
  /** Structural kind — drives node coloring (`--kind-*`) in the canvas. */
  kind: StoryNodeKind;
  /** Storage id used by the project store. Present for real scene nodes. */
  sceneId?: string;
  /** Public Lunascript branch key used by @episode/@next, e.g. `main:01`. */
  branchKey?: string;
  /** Episode title from the @episode header, when present. */
  title?: string;
  /** True for a placeholder standing in for a referenced-but-unknown scene id. */
  missing?: boolean;
  /** For `kind: "ending"` nodes, the LS ending type (complete / … ). */
  endingType?: string;
}

/** Semantic flavor of an edge — drives edge coloring/style in the canvas. */
export type StoryEdgeKind = "next" | "choice" | "gate" | "end" | "dangling";

/** A directed edge between two story nodes. */
export interface StoryEdge {
  /** Stable, deterministic edge id. */
  id: string;
  /** Source node id. */
  source: string;
  /** Target node id. */
  target: string;
  /** Optional label (option text, gate condition, ending type). */
  label?: string;
  /** Semantic flavor (default `"next"`). */
  kind?: StoryEdgeKind;
}

/** The built graph. */
export interface StoryGraph {
  nodes: StoryNode[];
  edges: StoryEdge[];
}

/** One input scene: a scene id and its raw `.ls` source. */
export interface SceneInput {
  id: string;
  source: string;
}

/** Synthetic node id for an `@end <type>` reached from a given scene. */
export function endingNodeId(sceneId: string, endingType: string, index: number): string {
  return `__end:${sceneId}:${endingType || "end"}:${index}`;
}

/** Synthetic node id for a referenced-but-unknown scene id. */
export function missingNodeId(target: string): string {
  return `__missing:${target}`;
}

const ENDING_TYPES = new Set(["complete", "to_be_continued", "bad_ending"]);

// A branch_key target: letters/digits and the path separators LS allows
// (`:` segment, `/` route path, `_`, `-`, `.`). Deliberately permissive — the
// builder validates against the known scene-id set, not against this shape.
const TARGET = "[A-Za-z0-9_./:-]+";

/** Parse the public routing id + title from the @episode header. */
function episodeMeta(source: string, fallbackId: string): { branchKey: string; title?: string } {
  const m = source.match(/@episode\s+(\S+)(?:\s+"([^"]*)")?/);
  const branchKey = m?.[1] || fallbackId;
  return { branchKey, ...(m?.[2] !== undefined ? { title: m[2] } : {}) };
}

/** Find the body of the LAST `@gate { ... }` block in a source, brace-matched. */
function extractGateBody(source: string): string | null {
  // Scan for the last `@gate` keyword, then brace-match its block. Using the
  // last occurrence honors the spec ("@gate must sit at the tail, one per
  // episode") while tolerating the keyword appearing in a comment earlier.
  const re = /@gate\b/g;
  let lastIndex = -1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source)) !== null) lastIndex = m.index;
  if (lastIndex < 0) return null;

  const open = source.indexOf("{", lastIndex);
  if (open < 0) return null;
  return matchBraces(source, open);
}

/**
 * Return the text BETWEEN the brace at `openIndex` and its matching close,
 * or `null` if unbalanced. Tolerant: braces inside string literals are NOT
 * specially handled (LS targets/labels don't contain braces), keeping this
 * cheap and predictable.
 */
function matchBraces(source: string, openIndex: number): string | null {
  let depth = 0;
  for (let i = openIndex; i < source.length; i++) {
    const ch = source[i];
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return source.slice(openIndex + 1, i);
    }
  }
  return null;
}

/** Collect every `@option <ID> <brave|safe> "<label>" { ... }` in a source. */
interface OptionBlock {
  id: string;
  label: string;
  body: string;
}
function extractOptions(source: string): OptionBlock[] {
  const out: OptionBlock[] = [];
  const re = /@option\s+(\S+)\s+(?:brave|safe)\s+"([^"]*)"\s*\{/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source)) !== null) {
    const open = m.index + m[0]!.length - 1; // index of the `{`
    const body = matchBraces(source, open);
    if (body === null) continue; // unterminated option block — skip, never throw
    out.push({ id: m[1]!, label: m[2]!, body });
  }
  return out;
}

/** Blank out every `@choice { ... }` block so a fallback flow scan doesn't
 *  re-read option routes already handled as choice branches. Returns the source
 *  with each choice block's contents replaced by spaces (length preserved). */
function stripChoiceBlocks(source: string): string {
  const re = /@choice\s*\{/g;
  let out = source;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source)) !== null) {
    const open = m.index + m[0]!.length - 1; // index of the `{`
    const body = matchBraces(source, open);
    if (body === null) continue;
    const start = open + 1;
    const end = start + body.length;
    out = out.slice(0, start) + " ".repeat(end - start) + out.slice(end);
  }
  return out;
}

/** All `@next <target>` targets appearing in a chunk of text, in order. */
function nextTargets(text: string): string[] {
  const re = new RegExp(`@next\\s+(${TARGET})`, "g");
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[1]!);
  return out;
}

/** All `@end <type>` ending types appearing in a chunk of text, in order. */
function endTypes(text: string): string[] {
  const re = /@end\s+([A-Za-z_]+)/g;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const t = m[1]!;
    out.push(ENDING_TYPES.has(t) ? t : "end");
  }
  return out;
}

/**
 * Build a story-structure graph from a set of scenes.
 *
 * One `scene` node per input scene (in input order). Edges are derived from
 * each scene's `@gate`/`@choice` exits:
 *   - `@next <id>` → an edge scene→id (`kind: "gate"` when inside the gate,
 *     `"choice"` when inside a `@choice` option, else `"next"`);
 *   - `@end <type>` → an edge to a synthetic ending node (`kind: "end"`);
 *   - a `@next` whose target is not a known scene id → an edge to a `missing`
 *     placeholder node (`kind: "dangling"`).
 *
 * Never throws; tolerates malformed, partial, or empty sources.
 */
export function buildStoryGraph(scenes: SceneInput[]): StoryGraph {
  const nodes: StoryNode[] = [];
  const edges: StoryEdge[] = [];

  // De-dupe maps so repeated targets/endings reuse one synthetic node.
  const sceneIds = new Set<string>();
  const missingNodes = new Map<string, StoryNode>();
  const edgeIds = new Set<string>();

  const list = Array.isArray(scenes) ? scenes : [];

  // First pass: a node per real scene + register known ids.
  for (const s of list) {
    if (!s || typeof s.id !== "string" || s.id === "") continue;
    const meta = episodeMeta(typeof s.source === "string" ? s.source : "", s.id);
    if (sceneIds.has(meta.branchKey)) continue;
    sceneIds.add(meta.branchKey);
    nodes.push({
      id: meta.branchKey,
      label: meta.title || meta.branchKey,
      kind: "scene",
      sceneId: s.id,
      branchKey: meta.branchKey,
      ...(meta.title ? { title: meta.title } : {}),
    });
  }

  /** Push an edge with a deterministic, de-duplicated id. */
  function addEdge(source: string, target: string, kind: StoryEdgeKind, label?: string): void {
    // Make the id unique per (source,target,kind,label) so two distinct option
    // branches to the same scene both survive, but exact dupes collapse.
    const base = `e:${source}->${target}:${kind}:${label ?? ""}`;
    let id = base;
    let n = 1;
    while (edgeIds.has(id)) id = `${base}#${n++}`;
    edgeIds.add(id);
    edges.push({ id, source, target, kind, ...(label ? { label } : {}) });
  }

  /** Ensure a placeholder node exists for an unknown `@next` target. */
  function ensureMissing(target: string): string {
    const id = missingNodeId(target);
    if (!missingNodes.has(id)) {
      const node: StoryNode = { id, label: target, kind: "missing", missing: true };
      missingNodes.set(id, node);
      nodes.push(node);
    }
    return id;
  }

  /** Resolve a `@next` target to a node id (known scene or missing placeholder). */
  function resolveNext(from: string, target: string, kind: StoryEdgeKind, label?: string): void {
    if (sceneIds.has(target)) {
      addEdge(from, target, kind, label);
    } else {
      addEdge(from, ensureMissing(target), "dangling", label ?? target);
    }
  }

  // Second pass: derive edges per scene.
  for (const s of list) {
    if (!s || typeof s.id !== "string" || s.id === "") continue;
    const source = typeof s.source === "string" ? s.source : "";
    if (source === "") continue;
    const sourceId = episodeMeta(source, s.id).branchKey;

    let endingCount = 0;
    const structure = buildEpisodeStructure(source);

    // (a) Legacy @choice option exits. A modern story map is episode-level and
    // should be driven by the tail @gate. Only fall back to option-body exits
    // when there is no parseable gate, preserving old partial scripts without
    // mixing local choice branches into the global episode map.
    if (!structure.gate || structure.gate.routes.length === 0) {
      for (const opt of extractOptions(source)) {
        for (const target of nextTargets(opt.body)) {
          if (sceneIds.has(target)) {
            addEdge(sourceId, target, "choice", opt.label);
          } else {
            addEdge(sourceId, ensureMissing(target), "dangling", opt.label);
          }
        }
        for (const _type of endTypes(opt.body)) {
          const id = endingNodeId(sourceId, _type, endingCount++);
          nodes.push({ id, label: endingTypeLabel(_type), kind: "ending", endingType: _type });
          addEdge(sourceId, id, "end", opt.label);
        }
      }
    }

    // (b) @gate exits. The source-structure model preserves route order and
    // labels, so story-map edges can show why a route is taken instead of just
    // drawing anonymous arrows. If there is no parseable gate block, fall back
    // to the older tolerant regex scan with @choice option blocks stripped.
    if (structure.gate && structure.gate.routes.length > 0) {
      for (const route of structure.gate.routes) {
        const label = humanizeRouteCondition(route, structure.choices) || route.condition || (route.isFallback ? "兜底" : undefined);
        if (route.outcome.kind === "next") {
          resolveNext(sourceId, route.outcome.value, "gate", label);
        } else {
          const id = endingNodeId(sourceId, route.outcome.value, endingCount++);
          nodes.push({ id, label: endingTypeLabel(route.outcome.value), kind: "ending", endingType: route.outcome.value });
          addEdge(sourceId, id, "end", label ?? route.outcome.value);
        }
      }
      continue;
    }

    // (c) Legacy fallback exits. The gate body carries the canonical routes; if there is
    //     no gate block, fall back to scanning the source for bare exits — but
    //     with @choice option blocks stripped, so the option routes handled in
    //     (a) aren't double-counted.
    const gateBody = extractGateBody(source);
    const flowText = gateBody ?? stripChoiceBlocks(source);
    const gateKind: StoryEdgeKind = gateBody ? "gate" : "next";

    for (const target of nextTargets(flowText)) {
      resolveNext(sourceId, target, gateKind);
    }
    for (const type of endTypes(flowText)) {
      const id = endingNodeId(sourceId, type, endingCount++);
      nodes.push({ id, label: endingTypeLabel(type), kind: "ending", endingType: type });
      addEdge(sourceId, id, "end", type);
    }
  }

  return { nodes, edges };
}

/** Short human label for an ending node. */
function endingTypeLabel(type: string): string {
  switch (type) {
    case "complete":
      return "结局 · 全剧终";
    case "to_be_continued":
      return "结局 · 待续";
    case "bad_ending":
      return "结局 · 坏结局";
    default:
      return "结局";
  }
}
